import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Card, Input, Select, Slider, Spin, Typography, Modal, message,
  Collapse, Tag, Button, Space, Tooltip, Avatar
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  MessageOutlined,
  SettingOutlined,
  CloseOutlined,
  CheckOutlined,
  SendOutlined,
  CopyOutlined,
  ShareAltOutlined,
  RobotOutlined,
  UserOutlined,
  MenuOutlined
} from '@ant-design/icons';
import { toast } from 'react-hot-toast';
import api from "../interceptors/api";
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import StandardButton from "../components/StandardButton";
import ProtectedToolWrapper from '../components/ProtectedToolWrapper';
import { getReasoningSupport } from '../utils/reasoningSupport';

const { TextArea } = Input;
const { Option } = Select;

const modelsByProvider = {
  OpenAI: [
    'gpt-5.4', 'gpt-5.4-mini', 'gpt-5.4-nano',
    'gpt-5.3-chat-latest', 'gpt-5.2-chat-latest', 'gpt-5.1-chat-latest',
    'gpt-5', 'gpt-5-mini', 'gpt-5-nano',
    'gpt-4.1', 'gpt-4.1-mini', 'gpt-4.1-nano',
    'gpt-4o', 'gpt-4o-mini',
    'o3', 'o4-mini',
  ],
  Groq: [
    'llama-3.3-70b-versatile',
    'llama-3.1-8b-instant',
    'openai/gpt-oss-120b',
    'openai/gpt-oss-20b',
    'qwen/qwen3-32b',
    'deepseek-r1-distill-llama-70b',
    'moonshotai/kimi-k2-instruct-0905',
    'groq/compound',
    'groq/compound-mini',
    'allam-2-7b',
  ],
  XAI: [
    'grok-4',
    'grok-3', 'grok-3-fast',
    'grok-3-mini', 'grok-3-mini-fast',
  ],
};

const extractResponseText = (response) => {
  if (!response) return '';
  if (response?.choices?.[0]?.message?.content) {
    return response.choices[0].message.content;
  } else if (response?.candidates?.[0]?.content?.parts?.[0]?.text) {
    return response.candidates[0].content.parts[0].text;
  } else if (typeof response?.text === 'string') {
    return response.text;
  } else if (typeof response === 'string') {
    return response;
  }
  return '';
};

const containsMarkdown = (text) => {
  if (!text || typeof text !== 'string') return false;
  const markdownPatterns = [
    /^#+\s/, /^\*\*.*\*\*/, /^\*.*\*/, /`[^`]+`/, /```[\s\S]*?```/,
    /^- /m, /^\d+\. /m, /\[.+\]\(.+\)/, /!\[.+\]\(.+\)/, />\s/, /\|.+\|/
  ];
  return markdownPatterns.some(pattern => pattern.test(text));
};

const ChatMessage = ({ message }) => {
  const isUser = message.role === 'user';
  const content = typeof message.content === 'string'
    ? message.content
    : JSON.stringify(message.content || '');

  const formattedTime = message.created_at && typeof message.created_at === 'string'
    ? new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : '';

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-6`}>
      <div className={`flex max-w-[80%] ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
        <div className={`flex-shrink-0 ${isUser ? 'ml-3' : 'mr-3'}`}>
          <Avatar
            icon={isUser ? <UserOutlined /> : <RobotOutlined />}
            className={isUser ? 'bg-blue-500' : 'bg-purple-500'}
            size="default"
          />
        </div>
        <div className="flex-1">
          <div className={`flex items-center mb-1 ${isUser ? 'justify-end' : 'justify-start'}`}>
            <span className="text-xs font-medium text-gray-600">
              {isUser ? 'You' : 'Assistant'}
            </span>
          </div>
          <div
            className={`rounded-2xl p-4 ${isUser
              ? 'bg-blue-500 text-white rounded-tr-none'
              : 'bg-gray-100 text-gray-800 rounded-tl-none'
              }`}
          >
            {containsMarkdown(content) && !isUser ? (
              <div className="markdown-content prose prose-sm max-w-none">
                <ReactMarkdown
                  components={{
                    h1: ({ node, ...props }) => <h1 className="text-xl font-bold mt-2 mb-1" {...props} />,
                    h2: ({ node, ...props }) => <h2 className="text-lg font-bold mt-2 mb-1" {...props} />,
                    h3: ({ node, ...props }) => <h3 className="text-base font-bold mt-1 mb-1" {...props} />,
                    p: ({ node, ...props }) => <p className="mb-2 text-sm" {...props} />,
                    ul: ({ node, ...props }) => <ul className="list-disc pl-4 mb-2 text-sm" {...props} />,
                    ol: ({ node, ...props }) => <ol className="list-decimal pl-4 mb-2 text-sm" {...props} />,
                    li: ({ node, ...props }) => <li className="mb-1 text-sm" {...props} />,
                    code: ({ node, inline, ...props }) =>
                      inline ?
                        <code className="bg-gray-200 px-1 py-0.5 rounded text-xs font-mono" {...props} /> :
                        <pre className="bg-gray-200 p-3 rounded my-2 overflow-x-auto"><code className="text-xs font-mono" {...props} /></pre>,
                    a: ({ node, ...props }) => <a className="text-blue-600 hover:underline text-sm" target="_blank" rel="noopener noreferrer" {...props} />,
                  }}
                >
                  {content}
                </ReactMarkdown>
              </div>
            ) : (
              <div className="whitespace-pre-wrap break-words text-sm">
                {content}
              </div>
            )}
          </div>
          <div className={`text-xs text-gray-400 mt-1 ${isUser ? 'text-right' : 'text-left'}`}>
            {formattedTime}
          </div>
        </div>
      </div>
    </div>
  );
};

const ChatSidebar = ({
  chats,
  currentChat,
  onSelectChat,
  onNewChat,
  onDeleteChat,
  onUpdateChat,
  onShareChat,
  loading,
  collapsed,
  onToggleCollapse
}) => {
  const { t } = useTranslation();
  const [editingChat, setEditingChat] = useState(null);
  const [editTitle, setEditTitle] = useState('');

  const handleEditStart = (chat, e) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingChat(chat.id);
    setEditTitle(typeof chat.title === 'string' ? chat.title : '');
  };

  const handleEditSave = async (chatId, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (editTitle.trim()) {
      await onUpdateChat(chatId, { title: editTitle.trim() });
    }
    setEditingChat(null);
  };

  const handleEditCancel = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingChat(null);
  };

  const handleDelete = (chatId, e) => {
    e.preventDefault();
    e.stopPropagation();
    onDeleteChat(chatId);
  };

  const handleShare = (chat, e) => {
    e.preventDefault();
    e.stopPropagation();
    onShareChat(chat);
  };

  const handleChatSelect = (chat, e) => {
    e.preventDefault();
    onSelectChat(chat);
  };

  if (collapsed) {
    return (
      <div className="w-16 border-r border-gray-200 h-full flex flex-col bg-gray-50 items-center py-4">
        <Button
          type="text"
          icon={<MenuOutlined />}
          onClick={onToggleCollapse}
          className="mb-4"
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => onNewChat()}
          className="mb-4"
          shape="circle"
        />
        <div className="flex-1 overflow-y-auto w-full px-2">
          {chats.map(chat => {
            const safeTitle = typeof chat.title === 'string' ? chat.title : 'Untitled';
            return (
              <Tooltip key={chat.id} title={safeTitle} placement="right">
                <div
                  className={`w-10 h-10 rounded-lg mb-2 cursor-pointer flex items-center justify-center ${currentChat?.id === chat.id
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                  onClick={(e) => handleChatSelect(chat, e)}
                >
                  {safeTitle.charAt(0).toUpperCase()}
                </div>
              </Tooltip>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="w-80 border-r border-gray-200 h-full flex flex-col bg-gray-50">
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => onNewChat()}
          block
          size="middle"
          className="font-medium mr-2"
        >
          {t('new_chat')}
        </Button>
        <Button
          type="text"
          icon={<MenuOutlined />}
          onClick={onToggleCollapse}
        />
      </div>
      <div className="flex-1 overflow-y-auto p-2">
        {loading ? (
          <div className="flex justify-center items-center h-32">
            <Spin />
          </div>
        ) : chats.length === 0 ? (
          <div className="text-center py-8 px-4">
            <MessageOutlined className="text-4xl text-gray-300 mb-3" />
            <p className="text-gray-500 text-sm">{t('no_chats_yet')}</p>
            <p className="text-gray-400 text-xs mt-1">{t('create_first_chat')}</p>
          </div>
        ) : (
          <div className="space-y-1">
            {chats.map(chat => {
              const safeTitle = typeof chat.title === 'string' ? chat.title : 'Untitled Chat';
              const safeUpdatedAt = chat.updated_at && typeof chat.updated_at === 'string'
                ? new Date(chat.updated_at).toLocaleDateString()
                : '';
              return (
                <div
                  key={chat.id}
                  className={`group relative p-3 rounded-lg cursor-pointer transition-all ${currentChat?.id === chat.id
                    ? 'bg-blue-50 border border-blue-200 shadow-sm'
                    : 'hover:bg-gray-100'
                    }`}
                  onClick={(e) => handleChatSelect(chat, e)}
                >
                  {editingChat === chat.id ? (
                    <div className="flex items-center" onClick={e => e.stopPropagation()}>
                      <Input
                        size="small"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        autoFocus
                        onPressEnter={(e) => handleEditSave(chat.id, e)}
                        className="flex-1 mr-1"
                      />
                      <Button
                        type="text"
                        size="small"
                        icon={<CheckOutlined />}
                        onClick={(e) => handleEditSave(chat.id, e)}
                        className="text-green-600"
                      />
                      <Button
                        type="text"
                        size="small"
                        icon={<CloseOutlined />}
                        onClick={handleEditCancel}
                        className="text-red-600"
                      />
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate pr-6">
                            {safeTitle}
                          </div>
                          <div className="text-xs text-gray-400 mt-1">
                            {safeUpdatedAt}
                          </div>
                        </div>
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                          <Tooltip title="Edit">
                            <Button
                              type="text"
                              size="small"
                              icon={<EditOutlined />}
                              onClick={(e) => handleEditStart(chat, e)}
                              className="text-gray-500 hover:text-blue-500"
                            />
                          </Tooltip>
                          <Tooltip title="Share">
                            <Button
                              type="text"
                              size="small"
                              icon={<ShareAltOutlined />}
                              onClick={(e) => handleShare(chat, e)}
                              className="text-gray-500 hover:text-green-500"
                            />
                          </Tooltip>
                          <Tooltip title="Delete">
                            <Button
                              type="text"
                              size="small"
                              icon={<DeleteOutlined />}
                              onClick={(e) => handleDelete(chat.id, e)}
                              className="text-gray-500 hover:text-red-500"
                            />
                          </Tooltip>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const SettingsPanel = ({ temperature, setTemperature, topP, setTopP, maxTokens, setMaxTokens }) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  return (
    <>
      <Tooltip title={t('settings')}>
        <Button
          icon={<SettingOutlined />}
          onClick={() => setVisible(true)}
          type="text"
        />
      </Tooltip>
      <Modal
        title={t('settings')}
        open={visible}
        onCancel={() => setVisible(false)}
        footer={[
          <Button key="close" onClick={() => setVisible(false)}>
            {t('close')}
          </Button>
        ]}
        width={400}
      >
        <div className="space-y-6 py-4">
          <div>
            <div className="flex justify-between mb-2">
              <label className="font-medium">{t('temperature')}</label>
              <span className="text-blue-600">{temperature}</span>
            </div>
            <Slider
              min={0}
              max={1}
              step={0.1}
              value={temperature}
              onChange={setTemperature}
              tooltip={{ open: false }}
            />
            <p className="text-xs text-gray-500 mt-1">
              Controls randomness: Lower is more focused, higher is more creative
            </p>
          </div>
          <div>
            <div className="flex justify-between mb-2">
              <label className="font-medium">{t('top_p')}</label>
              <span className="text-blue-600">{topP}</span>
            </div>
            <Slider
              min={0}
              max={1}
              step={0.1}
              value={topP}
              onChange={setTopP}
              tooltip={{ open: false }}
            />
            <p className="text-xs text-gray-500 mt-1">
              Nucleus sampling: Controls diversity of output
            </p>
          </div>
          <div>
            <div className="flex justify-between mb-2">
              <label className="font-medium">{t('max_tokens')}</label>
              <span className="text-blue-600">{maxTokens}</span>
            </div>
            <Slider
              min={100}
              max={10000}
              step={50}
              value={maxTokens}
              onChange={setMaxTokens}
              tooltip={{ open: false }}
            />
            <p className="text-xs text-gray-500 mt-1">
              Maximum length of the response
            </p>
          </div>
        </div>
      </Modal>
    </>
  );
};

const MultiModelPlayGround = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const hasCreatedInitialChat = useRef(false);
  const hasLoadedChats = useRef(false);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [chats, setChats] = useState([]);
  const [currentChat, setCurrentChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingChats, setLoadingChats] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [hasUnsavedChat, setHasUnsavedChat] = useState(false);
  const [currentPrompt, setCurrentPrompt] = useState('');
  const [temperature, setTemperature] = useState(0.7);
  const [topP, setTopP] = useState(1);
  const [maxTokens, setMaxTokens] = useState(1000);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // ---- State for delete confirmation modal ----
  const [deleteConfirm, setDeleteConfirm] = useState({ visible: false, chatId: null });

  // Check login status
  useEffect(() => {
    const userProfileData = JSON.parse(localStorage.getItem('userProfileData') || 'null');
    setIsLoggedIn(!!userProfileData?.id);
  }, []);

  // Load chats on mount (only once, after login status is known)
  useEffect(() => {
    if (isLoggedIn && !hasLoadedChats.current) {
      loadChats();
    }
  }, [isLoggedIn]);

  // Load shared data if share parameter exists
  useEffect(() => {
    const shareId = searchParams.get("share");
    if (shareId && isLoggedIn) {
      loadSharedData(shareId);
    }
  }, [searchParams, isLoggedIn]);

  // Auto-create first chat only after we've tried loading and found no chats
  useEffect(() => {
    if (isLoggedIn && chats.length === 0 && !loadingChats && hasLoadedChats.current && !hasCreatedInitialChat.current) {
      hasCreatedInitialChat.current = true;
      createNewChat();
    }
  }, [isLoggedIn, chats.length, loadingChats]);

  // ------------------------------------------------------------
  // Load chats with proper authentication headers
  // ------------------------------------------------------------
  const loadChats = async () => {
    try {
      setLoadingChats(true);
      const userProfileData = JSON.parse(localStorage.getItem('userProfileData') || 'null');
      if (!userProfileData?.id) {
        toast.error(t('login_required'));
        return;
      }

      const res = await api.get('/multi-model-playground/chats');
      if (res.data.success) {
        setChats(res.data.chats || []);
      } else {
        toast.error(t('failed_to_load_chats'));
      }
    } catch (err) {
      toast.error(err.response?.data?.error || t('failed_to_load_chats'));
    } finally {
      setLoadingChats(false);
      hasLoadedChats.current = true;
    }
  };

  const loadSharedData = async (shareId) => {
    try {
      const res = await api.get(`/multi-model-playground/share/${shareId}`);
      const data = res.data;
      setCurrentPrompt(data.prompt || '');
      setTemperature(data.temperature || 0.7);
      setTopP(data.top_p || 1);
      setMaxTokens(data.max_tokens || 1000);

      if (isLoggedIn) {
        createNewChat(data.provider, data.model);
        toast.success(t('shared_config_loaded'));
      }
    } catch (err) {
      toast.error(t('server_error'));
    }
  };

  const selectChat = async (chat) => {
    if (currentChat?.id === chat.id) return;

    setCurrentChat(chat);
    setHasUnsavedChat(false);

    try {
      const res = await api.get(`/multi-model-playground/chats/${chat.id}`);
      if (res.data.success) {
        setMessages(res.data.messages || []);
      }
    } catch (err) {
      if (err.response?.status !== 404) {
        message.error(t('failed_to_load_messages'));
      }
    }
  };

  const createNewChat = async (provider = 'OpenAI', model = 'gpt-4.1') => {
    if (hasUnsavedChat && currentChat && currentChat.id.toString().startsWith('temp-')) {
      return;
    }

    try {
      const userProfileData = JSON.parse(localStorage.getItem('userProfileData') || 'null');
      if (!userProfileData?.id) {
        toast.error(t('login_required'));
        return;
      }

      const tempChat = {
        id: `temp-${Date.now()}`,
        title: `New Chat ${chats.length + 1}`,
        provider,
        model,
        reasoning_effort: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        isTemp: true
      };

      setChats(prev => [tempChat, ...prev]);
      setCurrentChat(tempChat);
      setMessages([]);
      setCurrentPrompt('');
      setHasUnsavedChat(true);

    } catch (err) {
      message.error(err.response?.data?.error || t('failed_to_create_chat'));
    }
  };

  // ------------------------------------------------------------
  // Delete chat with controlled modal
  // ------------------------------------------------------------
  const deleteChat = (chatId) => {
    // Handle temporary chats immediately
    if (chatId.toString().startsWith('temp-')) {
      const updatedChats = chats.filter(c => c.id !== chatId);
      setChats(updatedChats);
      if (currentChat?.id === chatId) {
        if (updatedChats.length > 0) {
          selectChat(updatedChats[0]);
        } else {
          setCurrentChat(null);
          setMessages([]);
          setHasUnsavedChat(false);
          createNewChat();
        }
      }
      message.success(t('chat_deleted'));
      return;
    }

    // For persisted chats, open the controlled modal
    setDeleteConfirm({ visible: true, chatId });
  };

  const handleDeleteConfirmed = async () => {
    const chatId = deleteConfirm.chatId;
    setDeleteConfirm({ visible: false, chatId: null });

    try {
      const userProfileData = JSON.parse(localStorage.getItem('userProfileData') || 'null');
      if (!userProfileData?.id) {
        toast.error(t('login_required'));
        return;
      }

      const numericChatId = Number(chatId);
      if (isNaN(numericChatId)) {
        message.error('Invalid chat ID');
        return;
      }

      const res = await api.delete(`/multi-model-playground/chats/${numericChatId}`);

      if (res.data.success) {
        const updatedChats = chats.filter(c => c.id !== chatId);
        setChats(updatedChats);

        if (currentChat?.id === chatId) {
          if (updatedChats.length > 0) {
            selectChat(updatedChats[0]);
          } else {
            setCurrentChat(null);
            setMessages([]);
            setHasUnsavedChat(false);
            createNewChat();
          }
        }
        message.success(t('chat_deleted'));
      } else {
        message.error(res.data.error || t('failed_to_delete_chat'));
      }
    } catch (err) {
      message.error(err.response?.data?.error || t('failed_to_delete_chat'));
    }
  };

  const updateChat = async (chatId, updates) => {
    try {
      if (chatId.toString().startsWith('temp-')) {
        setChats(chats.map(c =>
          c.id === chatId ? { ...c, ...updates } : c
        ));
        if (currentChat?.id === chatId) {
          setCurrentChat({ ...currentChat, ...updates });
        }
        return;
      }

      const res = await api.put(`/multi-model-playground/chats/${chatId}`, updates);
      if (res.data.success) {
        setChats(chats.map(c =>
          c.id === chatId ? { ...c, ...updates } : c
        ));
        if (currentChat?.id === chatId) {
          setCurrentChat({ ...currentChat, ...updates });
        }
      }
    } catch (err) {
      message.error(t('failed_to_update_chat'));
    }
  };

  const shareChat = async (chat) => {
    try {
      const userProfileData = JSON.parse(localStorage.getItem('userProfileData') || 'null');
      if (!userProfileData?.id) {
        toast.error(t('login_to_share'));
        return;
      }

      if (chat.id.toString().startsWith('temp-')) {
        toast.error('Please send a message first before sharing');
        return;
      }

      const res = await api.post(`/multi-model-playground/chat/${chat.id}/share`);

      if (res.data.id) {
        const url = `${window.location.origin}/tools/multi-model-playground?share=${res.data.id}`;
        await navigator.clipboard.writeText(url);
        toast.success(t('share_success'));
      } else {
        toast.error(t('share_failed'));
      }
    } catch (err) {
      toast.error(err.response?.data?.error || t('share_failed'));
    }
  };

  const persistTempChat = async (tempChatId, provider, model, title) => {
    try {
      const userProfileData = JSON.parse(localStorage.getItem('userProfileData') || 'null');
      if (!userProfileData?.id) return null;

      const tempChat = chats.find(c => c.id === tempChatId);
      const res = await api.post('/multi-model-playground/chats', {
        provider,
        model,
        title,
        reasoning_effort: tempChat?.reasoning_effort ?? null,
      });

      if (res.data.success && res.data.chat) {
        const newChat = res.data.chat;

        setChats(prev => prev.map(c =>
          c.id === tempChatId ? newChat : c
        ));

        setCurrentChat(newChat);
        setHasUnsavedChat(false);

        return newChat;
      }
    } catch (_) {
    }
    return null;
  };

  const handleSendMessage = async () => {
    if (!currentPrompt.trim()) {
      return toast.error(t('please_enter_message'));
    }

    if (!currentChat) {
      return toast.error(t('select_chat_first'));
    }

    setSendingMessage(true);

    const userPrompt = currentPrompt;
    const isTempChat = currentChat.id.toString().startsWith('temp-');
    let actualChatId = currentChat.id;

    if (isTempChat) {
      const persistedChat = await persistTempChat(
        currentChat.id,
        currentChat.provider,
        currentChat.model,
        currentChat.title
      );
      if (persistedChat) {
        actualChatId = persistedChat.id;
      } else {
        toast.error('Failed to create chat');
        setSendingMessage(false);
        return;
      }
    }

    const tempUserMessage = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: userPrompt,
      created_at: new Date().toISOString()
    };

    setMessages(prev => [...prev, tempUserMessage]);
    setCurrentPrompt('');

    try {
      const userProfileData = JSON.parse(localStorage.getItem('userProfileData') || 'null');

      if (!userProfileData?.id) {
        toast.error(t('login_required'));
        setMessages(prev => prev.filter(m => m.id !== tempUserMessage.id));
        setSendingMessage(false);
        return;
      }

      const reasoningSupport = getReasoningSupport(currentChat.provider, currentChat.model);
      const res = await api.post('/multi-model-playground/chat', {
        chatId: actualChatId,
        provider: currentChat.provider,
        model: currentChat.model,
        prompt: userPrompt,
        temperature,
        top_p: topP,
        max_tokens: maxTokens,
        ...(reasoningSupport.supported && currentChat.reasoning_effort
          ? { reasoning_effort: currentChat.reasoning_effort }
          : {}),
      });

      const responseText = extractResponseText(res.data);

      const assistantMessage = {
        id: `resp-${Date.now()}`,
        role: 'assistant',
        content: responseText || 'No response content',
        created_at: new Date().toISOString()
      };
      setMessages(prev => [...prev, assistantMessage]);

      // Refresh chat list to update title if it was auto-generated
      loadChats();

    } catch (err) {
      setMessages(prev => prev.filter(m => m.id !== tempUserMessage.id));
      toast.error(err.response?.data?.error || t('api_request_failed'));
    } finally {
      setSendingMessage(false);
    }
  };

  if (!isLoggedIn) {
    return (
      <ProtectedToolWrapper showShareBanner={searchParams.has("share")}>
        <div className="flex items-center justify-center h-[calc(100vh-120px)]">
          <Card className="text-center p-8 max-w-md">
            <RobotOutlined className="text-5xl text-blue-500 mb-4" />
            <h2 className="text-2xl font-bold mb-2">{t('login_required')}</h2>
            <p className="text-gray-500 mb-6">{t('login_to_use_chat')}</p>
            <Button type="primary" size="large" href="/login">
              {t('login')}
            </Button>
          </Card>
        </div>
      </ProtectedToolWrapper>
    );
  }

  return (
    <ProtectedToolWrapper showShareBanner={searchParams.has("share")}>
      <div className="flex h-[calc(100vh-120px)] max-w-7xl mx-auto border border-gray-200 rounded-lg overflow-hidden bg-white shadow-lg">
        <ChatSidebar
          chats={chats}
          currentChat={currentChat}
          onSelectChat={selectChat}
          onNewChat={createNewChat}
          onDeleteChat={deleteChat}
          onUpdateChat={updateChat}
          onShareChat={shareChat}
          loading={loadingChats}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        />

        <div className="flex-1 flex flex-col bg-white">
          {currentChat ? (
            <div className="px-6 py-4 border-b border-gray-200 bg-white flex justify-between items-center">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">
                  {currentChat.isTemp && currentChat.title?.startsWith('New Chat')
                    ? 'New Chat'
                    : (typeof currentChat.title === 'string' ? currentChat.title : 'Untitled Chat')}
                </h2>
                <div className="flex items-center gap-2 mt-1">
                  <Tag color="blue">{currentChat.provider}</Tag>
                  <Tag color="green">{currentChat.model}</Tag>
                  <span className="text-xs text-gray-400 ml-2">
                    {messages.length} {messages.length === 1 ? 'message' : 'messages'}
                  </span>
                </div>
              </div>
              <Space size="middle">
                <Select
                  value={currentChat.provider}
                  onChange={(newProvider) => {
                    const defaultModel = modelsByProvider[newProvider]?.[0] || '';
                    updateChat(currentChat.id, {
                      provider: newProvider,
                      model: defaultModel,
                      reasoning_effort: null,
                    });
                  }}
                  style={{ width: 100 }}
                  size="small"
                  dropdownMatchSelectWidth={false}
                >
                  {Object.keys(modelsByProvider).map(prov => (
                    <Option key={prov} value={prov}>{prov}</Option>
                  ))}
                </Select>

                <Select
                  value={currentChat.model}
                  onChange={(newModel) => {
                    const nextSupport = getReasoningSupport(currentChat.provider, newModel);
                    const updates = { model: newModel };
                    if (!nextSupport.supported
                      || (currentChat.reasoning_effort
                        && !nextSupport.allowedEfforts.includes(currentChat.reasoning_effort))) {
                      updates.reasoning_effort = null;
                    }
                    updateChat(currentChat.id, updates);
                  }}
                  style={{ width: 150 }}
                  size="small"
                  dropdownMatchSelectWidth={false}
                >
                  {modelsByProvider[currentChat.provider]?.map(m => (
                    <Option key={m} value={m}>{m}</Option>
                  ))}
                </Select>

                {(() => {
                  const support = getReasoningSupport(currentChat.provider, currentChat.model);
                  if (!support.supported) return null;
                  return (
                    <Tooltip title={t('reasoning_effort_tooltip')}>
                      <Select
                        value={currentChat.reasoning_effort || 'off'}
                        onChange={(val) => {
                          updateChat(currentChat.id, {
                            reasoning_effort: val === 'off' ? null : val,
                          });
                        }}
                        style={{ width: 140 }}
                        size="small"
                        dropdownMatchSelectWidth={false}
                        aria-label={t('reasoning_effort')}
                      >
                        <Option value="off">{t('reasoning_effort_off')}</Option>
                        {support.allowedEfforts.map(eff => (
                          <Option key={eff} value={eff}>
                            {t(`reasoning_effort_${eff}`)}
                          </Option>
                        ))}
                      </Select>
                    </Tooltip>
                  );
                })()}

                <SettingsPanel
                  temperature={temperature}
                  setTemperature={setTemperature}
                  topP={topP}
                  setTopP={setTopP}
                  maxTokens={maxTokens}
                  setMaxTokens={setMaxTokens}
                />
              </Space>
            </div>
          ) : (
            <div className="px-6 py-4 border-b border-gray-200 bg-white">
              <h2 className="text-lg font-semibold text-gray-800">Multi-Model Chat</h2>
              <p className="text-sm text-gray-500 mt-1">Loading your chats...</p>
            </div>
          )}

          <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
            {!currentChat ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <MessageOutlined className="text-4xl text-blue-500" />
                </div>
                <h3 className="text-xl font-medium text-gray-700 mb-2">Welcome to Multi-Model Chat</h3>
                <p className="text-gray-500 mb-6 max-w-md">
                  Create a new chat to start conversing with different AI models.
                  Each chat maintains its own conversation history.
                </p>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => createNewChat()}
                  size="large"
                >
                  Create New Chat
                </Button>
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <MessageOutlined className="text-3xl text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-600 mb-2">No messages yet</h3>
                <p className="text-gray-400 mb-4 max-w-md">
                  Start a conversation with {currentChat.provider} - {currentChat.model}
                </p>
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                  <p className="text-sm text-gray-500">
                    Type your message below and press Send to begin
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((message, index) => (
                  <ChatMessage
                    key={message.id || index}
                    message={message}
                  />
                ))}
                {sendingMessage && (
                  <div className="flex justify-start">
                    <div className="flex items-center space-x-2">
                      <Avatar icon={<RobotOutlined />} className="bg-purple-500" />
                      <div className="bg-gray-100 rounded-2xl p-4 rounded-tl-none">
                        <Spin size="small" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {currentChat && (
            <div className="p-4 border-t border-gray-200 bg-white">
              <div className="flex items-end gap-2">
                <TextArea
                  rows={3}
                  value={currentPrompt}
                  onChange={e => setCurrentPrompt(e.target.value)}
                  placeholder={`Message ${currentChat.provider} - ${currentChat.model}...`}
                  onPressEnter={(e) => {
                    if (!e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  disabled={sendingMessage}
                  className="resize-none rounded-2xl border-gray-300 focus:border-blue-400"
                />
                <StandardButton
                  type="primary"
                  onClick={handleSendMessage}
                  loading={sendingMessage}
                  disabled={sendingMessage || !currentPrompt.trim()}
                  className="px-6 h-10 rounded-full"
                  icon={<SendOutlined />}
                >
                  Send
                </StandardButton>
              </div>
              <div className="flex items-center justify-between mt-2">
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <span>Shift + Enter for new line</span>
                </div>
                <div className="flex items-center gap-2">
                  <Tag color="blue">Temp: {temperature}</Tag>
                  <Tag color="purple">Top P: {topP}</Tag>
                  <Tag color="cyan">Max: {maxTokens}</Tag>
                  {currentChat?.reasoning_effort && (
                    <Tag color="magenta">
                      {t('reasoning_effort')}: {t(`reasoning_effort_${currentChat.reasoning_effort}`)}
                    </Tag>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete confirmation modal */}
      <Modal
        title={t('delete_chat_confirm')}
        open={deleteConfirm.visible}
        onOk={handleDeleteConfirmed}
        onCancel={() => setDeleteConfirm({ visible: false, chatId: null })}
        okText={t('delete')}
        cancelText={t('cancel')}
        okButtonProps={{ danger: true }}
      >
        <p>{t('delete_chat_warning')}</p>
      </Modal>
    </ProtectedToolWrapper>
  );
};

export default MultiModelPlayGround;